/**
 * Table Category Classification System
 * 
 * Resolves the union type crisis by categorizing core_crm tables into distinct groups
 * based on their structure and purpose, allowing appropriate type handling for each.
 */

// Complete list of all core_crm tables categorized by their structure and purpose
export const TABLE_CATEGORIES = {
  // Business data tables - Follow full BaseRecord pattern with all audit fields
  BUSINESS_DATA: [
    'app_user',
    'table_info', 
    'table_field',
    'app_group',
    'constraint_info',
    'publication_info',
    'table_publication',
    'picklist_field',
    'relationship_lookup',
    'email_template',
    'email_campaign',
    'email_campaign_send'
  ],
  
  // System log tables - Immutable records with partial BaseRecord compatibility
  // Have created_at/created_by but missing updated_at/updated_by (intentionally)
  SYSTEM_LOGS: [
    'change_event',           // Has: id, created_by, transaction_time, Missing: created_at, updated_at, updated_by
    'scheduled_sql_log',      // Has: id, created_by, created_at, Missing: updated_at, updated_by
    'auth_event'              // If it exists - logs are generally immutable
  ],
  
  // Analytics/tracking tables - Different structure, not BaseRecord compatible
  // These have completely different purposes and field patterns
  ANALYTICS: [
    'page_view',              // Has: user_id, table_info, record_id, viewed_at - No BaseRecord fields
    'search_history'          // Similar analytics pattern
  ]
} as const;

// Type-safe category names
export type TableCategory = 'business_data' | 'system_logs' | 'analytics' | 'user_defined';

// Flattened arrays for efficient lookup
const BUSINESS_DATA_TABLES = TABLE_CATEGORIES.BUSINESS_DATA;
const SYSTEM_LOG_TABLES = TABLE_CATEGORIES.SYSTEM_LOGS;
const ANALYTICS_TABLES = TABLE_CATEGORIES.ANALYTICS;

/**
 * Determines the category of a table based on its name
 * Returns 'user_defined' for any table not in core_crm or unknown core_crm tables
 */
export const getTableCategory = (tableName: string, schema: string = 'core_crm'): TableCategory => {
  // Only categorize core_crm tables - everything else is user_defined
  if (schema !== 'core_crm') {
    return 'user_defined';
  }
  
  if (BUSINESS_DATA_TABLES.includes(tableName as any)) {
    return 'business_data';
  }
  
  if (SYSTEM_LOG_TABLES.includes(tableName as any)) {
    return 'system_logs';
  }
  
  if (ANALYTICS_TABLES.includes(tableName as any)) {
    return 'analytics';
  }
  
  // Unknown core_crm table defaults to user_defined for safety
  return 'user_defined';
};

/**
 * Checks if a table follows the full BaseRecord pattern
 */
export const isBaseRecordCompatible = (tableName: string, schema: string = 'core_crm'): boolean => {
  const category = getTableCategory(tableName, schema);
  return category === 'business_data' || category === 'user_defined';
};

/**
 * Checks if a table is a system log (immutable, no updates allowed)
 */
export const isSystemLog = (tableName: string, schema: string = 'core_crm'): boolean => {
  return getTableCategory(tableName, schema) === 'system_logs';
};

/**
 * Checks if a table is analytics/tracking (flexible structure)
 */
export const isAnalyticsTable = (tableName: string, schema: string = 'core_crm'): boolean => {
  return getTableCategory(tableName, schema) === 'analytics';
};

/**
 * Returns allowed operations for a table category
 */
export const getAllowedOperations = (category: TableCategory): string[] => {
  switch (category) {
    case 'business_data':
    case 'user_defined':
      return ['select', 'insert', 'update', 'delete'];
    case 'system_logs':
      return ['select', 'insert']; // No updates/deletes for logs
    case 'analytics':
      return ['select', 'insert', 'delete']; // Updates rarely needed for analytics
    default:
      return ['select'];
  }
};

/**
 * Validates if an operation is allowed on a table
 */
export const isOperationAllowed = (
  operation: string, 
  tableName: string, 
  schema: string = 'core_crm'
): boolean => {
  const category = getTableCategory(tableName, schema);
  const allowedOps = getAllowedOperations(category);
  return allowedOps.includes(operation.toLowerCase());
};